import React, { createContext, useCallback, useContext, useRef, useState } from 'react';
import { Button, Typography } from '@mui/material';

import AppDialog from './AppDialog';

interface ConfirmOptions {
  title: string;
  message?: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmColor?: 'primary' | 'error' | 'warning';
}

type ConfirmFn = (options: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error('useConfirm must be used within a ConfirmProvider');
  return confirm;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ConfirmOptions>({ title: '' });
  const resolverRef = useRef<((value: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  // Keep `options` mounted while the dialog fades out so the text doesn't blank
  // mid-transition; only `open` flips on close.
  const settle = useCallback((result: boolean) => {
    resolverRef.current?.(result);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AppDialog
        open={open}
        onClose={() => settle(false)}
        title={options.title}
        maxWidth="xs"
        actions={
          <>
            <Button onClick={() => settle(false)}>{options.cancelLabel ?? 'Cancel'}</Button>
            <Button
              variant="contained"
              color={options.confirmColor ?? 'primary'}
              onClick={() => settle(true)}
            >
              {options.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
      >
        <Typography sx={{ fontSize: 14, color: 'text.secondary' }}>{options.message}</Typography>
      </AppDialog>
    </ConfirmContext.Provider>
  );
}
